/**
 * Wishlist ("save for later") feature, ported from the jewellerybyliam
 * theme's localStorage-based implementation.
 *
 * Stored in localStorage as an array of small product snapshots, so the
 * drawer renders instantly with no request per saved item. A Shopify theme
 * can't write to a customer record from the storefront - that needs an
 * Admin API call from a server - so this is per browser, not per account.
 *
 * The drawer's open/close/focus-trap behavior is handled entirely by this
 * theme's native <theme-drawer> component (see assets/theme-drawer.js) via
 * declarative on:click="#wishlist-drawer/toggle" attributes in the markup -
 * this file only owns the wishlist data and painting its contents.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const STORAGE_KEY = 'human-apparel:wishlist';

function readList() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const list = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(list)) return [];
    return list.filter((item) => item && item.handle);
  } catch (e) {
    return [];
  }
}

function writeList(list) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
  } catch (e) {
    /* private browsing or storage disabled; wishlist just won't persist */
  }
  paint();
}

function has(handle) {
  return readList().some((item) => item.handle === handle);
}

let toastTimeout;

function showToast(message) {
  const toast = document.querySelector('[data-wishlist-toast]');
  if (!toast) return;
  const textEl = toast.querySelector('[data-wishlist-toast-text]');
  if (textEl) textEl.textContent = message;

  toast.classList.add('is-visible');
  clearTimeout(toastTimeout);
  toastTimeout = setTimeout(() => {
    toast.classList.remove('is-visible');
  }, 2200);
}

function toggle(item) {
  const list = readList();
  const index = list.findIndex((x) => x.handle === item.handle);
  const added = index === -1;
  const title = item.title || 'Item';

  if (added) {
    list.unshift(item);
  } else {
    list.splice(index, 1);
  }
  writeList(list);
  showToast(added ? `${title} added to your wishlist` : `${title} removed from your wishlist`);
  return added;
}

function remove(handle) {
  const item = readList().find((x) => x.handle === handle);
  writeList(readList().filter((x) => x.handle !== handle));
  if (item) showToast(`${item.title || 'Item'} removed from your wishlist`);
}

/** Adds one wishlist item to the cart using the theme's own cart-update event contract, so the existing header cart bubble updates itself. */
function addToCart(item, button) {
  if (!item.variantId) {
    window.location.href = item.url || '/';
    return;
  }

  const originalText = button.textContent;
  button.disabled = true;
  button.textContent = 'Adding…';

  const deferredEventPromise = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'wishlist',
      lines: [{ merchandiseId: item.variantId, quantity: 1 }],
      promise: deferredEventPromise.promise,
    })
  );

  fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ items: [{ id: item.variantId, quantity: 1 }] }),
  })
    .then((response) => {
      if (!response.ok) {
        return response.json().then((error) => {
          throw new Error(error.description || 'Could not add to cart');
        });
      }
      return response.json();
    })
    .then(() => {
      button.textContent = 'Added';
      return fetch('/cart.js', { headers: { Accept: 'application/json' } });
    })
    .then((response) => response.json())
    .then((cart) => {
      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'wishlist',
          sourceId: item.handle,
          itemCount: cart.item_count,
          didError: false,
        },
      });
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 1600);
    })
    .catch((error) => {
      deferredEventPromise.reject(error);
      button.textContent = error && error.message ? 'Unavailable' : 'Try again';
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 2000);
    });
}

/** Repaints every surface that reflects the list: count badges, heart buttons, and the drawer body. */
function paint() {
  const list = readList();

  document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
    el.textContent = list.length > 99 ? '99+' : String(list.length);
  });

  document.querySelectorAll('[data-wishlist-bubble]').forEach((el) => {
    el.classList.toggle('visually-hidden', list.length === 0);
  });

  document.querySelectorAll('[data-wishlist-toggle]').forEach((button) => {
    const on = has(button.dataset.handle);
    button.setAttribute('aria-pressed', String(on));
    const title = button.dataset.title || 'this product';
    button.setAttribute(
      'aria-label',
      on ? `Remove ${title} from your wishlist` : `Save ${title} to your wishlist`
    );
  });

  const body = document.querySelector('[data-wishlist-body]');
  if (!body) return;

  const emptyState = document.querySelector('[data-wishlist-empty]');
  const footer = document.querySelector('[data-wishlist-footer]');
  if (emptyState) emptyState.hidden = list.length > 0;
  if (footer) footer.hidden = list.length === 0;

  body.textContent = '';
  list.forEach((item) => {
    const row = document.createElement('div');
    row.className = 'wishlist-drawer__item';

    const thumb = document.createElement('a');
    thumb.className = 'wishlist-drawer__thumb';
    thumb.href = item.url || '#';
    if (item.image) {
      const img = document.createElement('img');
      img.src = item.image;
      img.alt = '';
      img.loading = 'lazy';
      thumb.appendChild(img);
    }

    const info = document.createElement('div');
    info.className = 'wishlist-drawer__info';

    const link = document.createElement('a');
    link.className = 'wishlist-drawer__title';
    link.href = item.url || '#';
    link.textContent = item.title || item.handle;

    const price = document.createElement('p');
    price.className = 'wishlist-drawer__price';
    price.textContent = item.price || '';

    info.appendChild(link);
    info.appendChild(price);

    let action;
    if (item.available === false) {
      action = document.createElement('span');
      action.className = 'wishlist-drawer__action wishlist-drawer__action--out';
      action.textContent = 'Sold out';
    } else if ((item.variantCount || 1) > 1) {
      action = document.createElement('a');
      action.className = 'wishlist-drawer__action';
      action.href = item.url || '#';
      action.textContent = 'Choose options';
    } else {
      action = document.createElement('button');
      action.type = 'button';
      action.className = 'wishlist-drawer__action';
      action.textContent = 'Add to bag';
      action.addEventListener('click', () => addToCart(item, action));
    }
    info.appendChild(action);

    const removeButton = document.createElement('button');
    removeButton.type = 'button';
    removeButton.className = 'wishlist-drawer__remove button-unstyled';
    removeButton.setAttribute('aria-label', `Remove ${item.title || 'item'} from your wishlist`);
    removeButton.innerHTML =
      '<svg viewBox="0 0 16 16" aria-hidden="true" focusable="false"><path d="M11.1765 12.025 8 8.849l-3.1765 3.176M12.025 3.977 8.00078 7.99989 3.97652 3.97652" stroke="currentColor" stroke-width="1.1" fill="none" stroke-linecap="round"/></svg>';
    removeButton.addEventListener('click', () => remove(item.handle));

    row.appendChild(thumb);
    row.appendChild(info);
    row.appendChild(removeButton);
    body.appendChild(row);
  });
}

document.addEventListener('click', (event) => {
  const button = event.target.closest && event.target.closest('[data-wishlist-toggle]');
  if (!button) return;
  event.preventDefault();
  toggle({
    handle: button.dataset.handle,
    title: button.dataset.title,
    url: button.dataset.url,
    price: button.dataset.price,
    image: button.dataset.image,
    variantId: button.dataset.variantId,
    variantCount: parseInt(button.dataset.variantCount, 10) || 1,
    available: button.dataset.available === 'true',
  });
});

const clearButton = document.querySelector('[data-wishlist-clear]');
if (clearButton) {
  clearButton.addEventListener('click', () => writeList([]));
}

// Keeps other open tabs in step.
window.addEventListener('storage', (event) => {
  if (event.key === STORAGE_KEY) paint();
});

paint();

/** Public surface, in case a future backend sync needs to read/replace the list without touching this file. */
window.HumanApparelWishlist = {
  all: readList,
  has,
  toggle,
  remove,
  replace: (list) => writeList(Array.isArray(list) ? list : []),
  refresh: paint,
};
